
import { useStats } from '@/hooks/useStats';
import { Widget } from '../Widget';

interface UserCountWidgetProps {
  isEditMode?: boolean;
}

export function UserCountWidget({ isEditMode }: UserCountWidgetProps) {
  const { stats, isLoading } = useStats();

  if (isLoading) {
    return (
      <Widget id="user-count" title="User Statistics" isEditMode={isEditMode}>
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-slate-200 dark:bg-slate-700 rounded" />
          <div className="space-y-3">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
        </div>
      </Widget>
    );
  }

  const roleColors = {
    admin: 'text-purple-600 dark:text-purple-400',
    producer: 'text-blue-600 dark:text-blue-400',
    moderator: 'text-green-600 dark:text-green-400',
    congregant: 'text-slate-600 dark:text-slate-400',
  };

  return (
    <Widget id="user-count" title="User Statistics" isEditMode={isEditMode}>
      <div className="space-y-4">
        {/* Total users - large display */}
        <div className="text-center pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="text-4xl font-bold text-primary-600 dark:text-primary-400">
            {stats?.users.total ?? 0}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Total Users
          </div>
        </div>

        {/* Role breakdown */}
        <div className="space-y-3">
          {Object.entries(stats?.users.byRole ?? {}).map(([role, count]) => (
            <div
              key={role}
              className="flex items-center justify-between py-2 px-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg"
            >
              <span className="text-sm capitalize text-slate-700 dark:text-slate-300">
                {role}
              </span>
              <span className={`text-lg font-semibold ${roleColors[role as keyof typeof roleColors]}`}>
                {count}
              </span>
            </div>
          ))}
        </div>

        {/* Active/Inactive */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
          <div className="text-center">
            <div className="text-xl font-semibold text-green-600 dark:text-green-400">
              {stats?.users.active ?? 0}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Active
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-slate-400 dark:text-slate-500">
              {stats?.users.inactive ?? 0}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Inactive
            </div>
          </div>
        </div>
      </div>
    </Widget>
  );
}
